import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { sha256 } from '@/lib/crypto'
import {
  acceptAttestations,
  type AttestationConsent,
  type AttestationTemplate,
  fetchAttestations,
} from '@/models/attestation'

interface CarrierAttestationDialogProps {
  orgId: string
  carrierName?: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Fires once the consent is recorded, so the page can refetch and clear the banner. */
  onAccepted: () => void
}

/**
 * The carrier's own terms, asked for on its quote card rather than up front — only
 * customers who get this far are made to read them.
 */
export function CarrierAttestationDialog({
  orgId,
  carrierName,
  open,
  onOpenChange,
  onAccepted,
}: CarrierAttestationDialogProps) {
  const [templates, setTemplates] = useState<AttestationTemplate[]>([])
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError('')
    fetchAttestations(orgId)
      .then((loaded) => {
        if (!cancelled) setTemplates(loaded)
      })
      .catch(() => {
        if (!cancelled) setError("We couldn't load these terms. Please try again.")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, orgId])

  const allAccepted = templates.length > 0 && templates.every((t) => checked[t.attestation_type])

  async function onAccept() {
    setSaving(true)
    setError('')
    try {
      const attestedAt = new Date().toISOString()
      const consents: AttestationConsent[] = templates.map((template) => ({
        attestation_type: template.attestation_type,
        version: template.version,
        content_hash: sha256(template.rendered_text),
        bool_value: true,
        attested_at: attestedAt,
      }))
      await acceptAttestations(orgId, consents)
      onAccepted()
      onOpenChange(false)
    } catch {
      setError("We couldn't save your acceptance. Please try again.")
    } finally {
      setSaving(false)
    }
  }

  const title = carrierName ? `Accept ${carrierName}'s terms` : 'Accept the carrier terms'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            This carrier needs you to accept its terms before it can finalize your price.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-ink-400 text-sm">Loading terms…</p>
        ) : templates.length === 0 ? (
          <p className="text-ink-400 text-sm">There is nothing left to accept for this carrier.</p>
        ) : (
          <div className="flex max-h-96 flex-col gap-4 overflow-y-auto">
            {templates.length > 1 && (
              <label className="flex items-center gap-3 px-1 text-sm font-bold">
                <Checkbox
                  checked={allAccepted}
                  onCheckedChange={(value) =>
                    setChecked(Object.fromEntries(templates.map((t) => [t.attestation_type, !!value])))
                  }
                  className="size-5"
                />
                Accept all {templates.length}
              </label>
            )}
            {templates.map((template) => (
              <div
                key={template.attestation_type}
                className="border-primary/20 bg-muted/50 flex items-start gap-3 rounded-lg border p-3"
              >
                <Checkbox
                  checked={!!checked[template.attestation_type]}
                  onCheckedChange={(value) =>
                    setChecked((current) => ({ ...current, [template.attestation_type]: !!value }))
                  }
                  className="mt-0.5 size-5"
                />
                <div className="text-sm">
                  <p className="whitespace-pre-wrap">{template.rendered_text}</p>
                  {template.documents?.map((doc) => (
                    <a
                      key={doc.url}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-ink hover:underline"
                    >
                      {doc.name}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-destructive text-sm">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button loading={saving ? 'Saving…' : undefined} disabled={!allAccepted} onClick={onAccept}>
            Accept and continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
